import { NavLink } from "react-router-dom";
import { LayoutDashboard, GraduationCap, Layers, CalendarDays, ShieldCheck, LogOut, X, UserCircle, ClipboardCheck } from "lucide-react";
import { useAuth, hasPerm, moduleVisible, isEleve } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent hover:text-foreground"
  }`;

export function Sidebar({ open, onClose }) {
  const { user, modules, centre, logout } = useAuth();
  if (!user) return null;
  const eleve = isEleve(user);

  // « Tableau de bord » et « Administration » restent toujours visibles ;
  // Élèves / Modules / Planning dépendent À LA FOIS des permissions de la
  // personne et de l'interrupteur global piloté depuis le panel.
  const items = eleve
    ? [
        { to: "/", label: "Mon planning", icon: LayoutDashboard, show: true },
        { to: "/mon-suivi", label: "Mon suivi", icon: ClipboardCheck, show: true },
        { to: "/profil", label: "Mon profil", icon: UserCircle, show: true },
      ]
    : [
        { to: "/", label: "Tableau de bord", icon: LayoutDashboard, show: true },
        { to: "/eleves", label: "Élèves", icon: GraduationCap, show: moduleVisible(modules, "eleves") && hasPerm(user, "eleves", "view") },
        { to: "/modules", label: "Modules", icon: Layers, show: moduleVisible(modules, "modules") && hasPerm(user, "modules", "view") },
        { to: "/planning", label: "Planning", icon: CalendarDays, show: moduleVisible(modules, "planning") && hasPerm(user, "planning", "view") },
        { to: "/profil", label: "Mon profil", icon: UserCircle, show: true },
        { to: "/admin", label: "Administration", icon: ShieldCheck, show: user.is_admin },
      ];

  return (
    <>
      {open && <div onClick={onClose} data-testid="sidebar-overlay" className="fixed inset-0 z-40 bg-black/50 lg:hidden" />}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 left-0 z-50 w-72 lg:w-64 shrink-0 border-r border-border bg-card flex flex-col h-screen transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="px-5 py-6 flex items-center justify-between">
          <div className="min-w-0">
            <p className="font-heading text-xl font-bold tracking-tight">SOGO Formation</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{centre?.nom || "Suivi des cours"}</p>
          </div>
          <button onClick={onClose} data-testid="sidebar-close-button" className="lg:hidden p-1.5 rounded-md hover:bg-accent">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {items.filter((i) => i.show).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={onClose} className={linkClass} data-testid={`sidebar-link-${item.to.replace(/\//g, "-") || "home"}`}>
              <item.icon size={18} className="shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-2 py-2 mb-1">
            <p className="text-sm font-medium" data-testid="sidebar-user-name">{user.prenom} {user.nom}</p>
            <p className="text-xs text-muted-foreground">
              {user.is_admin ? "Administrateur" : eleve ? "Élève" : "Formateur"}
              {eleve && user.access_end ? ` · accès jusqu'au ${fmtDate(user.access_end)}` : ""}
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>
    </>
  );
}
